import { useCallback, useEffect, useState } from 'react';
import { Hash, Loader2, Send, Trash2, Plug, AlertCircle } from 'lucide-react';
import { api } from '../lib/api.js';

/**
 * Connects one app to one Slack channel.
 *
 * The bot token is write-only by design: it is a credential for the customer's
 * whole workspace, so the server never returns it. Once connected the field is
 * blank and re-entering it is how you change workspaces.
 */
export function SlackCard({ appId, canEdit }) {
  const [state, setState] = useState({ integration: null, inboundEnabled: false });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [channelId, setChannelId] = useState('');
  const [channelName, setChannelName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!appId) return;
    setLoading(true);
    try {
      setState(await api.slack.get(appId));
      setLoadError(null);
    } catch (err) {
      // Kept separate from `error`, and from the empty state: a failed read is
      // not the same fact as "no integration", and rendering it as one told
      // people with a live Slack channel that they had none.
      setState({ integration: null, inboundEnabled: false });
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    load();
  }, [load]);

  const { integration, inboundEnabled } = state;

  const connect = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.slack.connect(appId, {
        botToken: botToken.trim(),
        channelId: channelId.trim(),
        channelName: channelName.trim(),
      });
      setBotToken('');
      setOpen(false);
      setNotice('Connected. Check the channel — a confirmation was posted.');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleEvent = async (key, value) => {
    setBusy(true);
    try {
      const updated = await api.slack.setEvents(appId, { [key]: value });
      setState((current) => ({ ...current, integration: updated }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Unsubscribe from Slack? The stored bot token is deleted.')) return;
    setBusy(true);
    try {
      await api.slack.disconnect(appId);
      await load();
      setNotice('Unsubscribed. Nothing further will be posted to that channel.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.slack.test(appId);
      setNotice(result.posted ? 'Posted to the channel.' : `Slack refused it: ${result.error}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">
          <Hash size={13} /> Slack
        </span>
        {integration?.connected && (
          <span className="card__meta mono">
            {integration.channelName ? `#${integration.channelName}` : integration.channelId}
            {integration.teamName ? ` · ${integration.teamName}` : ''}
          </span>
        )}
      </div>

      {loading ? (
        <div className="card__foot">
          <Loader2 size={13} className="spin" /> Loading…
        </div>
      ) : (
        <>
          {loadError && (
            <div className="result result--warn">
              <AlertCircle size={13} /> Could not read this app&apos;s Slack settings:{' '}
              {loadError}. Any existing connection is still live — this is the dashboard
              failing to load it, not Slack being disconnected.
            </div>
          )}

          {!integration && !loadError && (
            <div className="card__foot">
              Post visitor arrivals and messages into a Slack channel, and reply to them from
              there without opening the dashboard.
            </div>
          )}

          {integration && !integration.connected && (
            <div className="result result--warn">
              <AlertCircle size={13} /> Slack disabled this connection: {integration.lastError}.
              Reconnect below once it is fixed.
            </div>
          )}

          {integration && (
            <div className="settings">
              {/* What this app is actually subscribed to, said plainly. The
                  toggles below are the subscription's contents; this row is
                  the subscription itself, and the one thing you look for when
                  asking "is Slack on for this app, and where does it go?" */}
              <div className="settings__row">
                <span>Subscribed channel</span>
                <span className="mono">
                  {integration.channelName
                    ? `#${integration.channelName}`
                    : integration.channelId}
                  {integration.teamName ? ` · ${integration.teamName}` : ''}
                </span>
              </div>

              <div className="settings__row">
                <span>Last posted</span>
                <span className="mono muted">
                  {integration.lastPostedAt
                    ? new Date(integration.lastPostedAt).toLocaleString()
                    : 'Nothing yet'}
                </span>
              </div>

              <label className="settings__row">
                <span>
                  When a visitor comes online
                  <span className="muted"> — starts a thread for them</span>
                </span>
                <input
                  type="checkbox"
                  className="switch"
                  checked={integration.events.onVisitorArrival}
                  disabled={!canEdit || busy}
                  onChange={(e) => toggleEvent('onVisitorArrival', e.target.checked)}
                />
              </label>

              <label className="settings__row">
                <span>
                  When a visitor sends a message
                  <span className="muted"> — posted into that visitor's thread</span>
                </span>
                <input
                  type="checkbox"
                  className="switch"
                  checked={integration.events.onNewMessage}
                  disabled={!canEdit || busy}
                  onChange={(e) => toggleEvent('onNewMessage', e.target.checked)}
                />
              </label>

              <label className="settings__row">
                <span>
                  Mirror agent replies
                  <span className="muted"> — keeps the thread a complete record</span>
                </span>
                <input
                  type="checkbox"
                  className="switch"
                  checked={integration.events.mirrorAgentReplies}
                  disabled={!canEdit || busy}
                  onChange={(e) => toggleEvent('mirrorAgentReplies', e.target.checked)}
                />
              </label>

              <div className="settings__row">
                <span>Replying from Slack</span>
                <span className="mono">{inboundEnabled ? 'Enabled' : 'Not configured'}</span>
              </div>

              {!inboundEnabled && (
                <div className="result result--warn">
                  Posting to Slack works, but replies typed in Slack will not reach the visitor
                  until <code>SLACK_SIGNING_SECRET</code> is set on the server. Without it an
                  incoming event cannot be told apart from a forged one.
                </div>
              )}

              {canEdit && (
                <div className="settings__row">
                  <span>Check the connection</span>
                  <span className="settings__control">
                    <button type="button" className="button" onClick={sendTest} disabled={busy}>
                      <Send size={13} /> Post a test
                    </button>
                    <button
                      type="button"
                      className="button button--danger"
                      onClick={disconnect}
                      disabled={busy}
                    >
                      <Trash2 size={13} /> Unsubscribe
                    </button>
                  </span>
                </div>
              )}

              {!canEdit && (
                <div className="muted">
                  Your access to this app is read-only for integration, so you can see this
                  subscription but not change or remove it. Unsubscribing deletes the stored
                  bot token, which needs edit access.
                </div>
              )}
            </div>
          )}

          {error && <div className="login__error">{error}</div>}
          {notice && <div className="result result--ok">{notice}</div>}

          {canEdit && !open && (
            <div className="card__actions">
              <button type="button" className="button" onClick={() => setOpen(true)}>
                <Plug size={13} /> {integration ? 'Reconnect' : 'Connect Slack'}
              </button>
            </div>
          )}

          {canEdit && open && (
            <form onSubmit={connect}>
              <div className="card__body">
                <label className="field">
                  <span className="field__label">BOT USER OAUTH TOKEN</span>
                  <input
                    className="field__control mono"
                    type="password"
                    required
                    autoComplete="off"
                    value={botToken}
                    onChange={(e) => setBotToken(e.target.value)}
                    placeholder="xoxb-…"
                  />
                </label>

                <label className="field">
                  <span className="field__label">CHANNEL ID</span>
                  <input
                    className="field__control mono"
                    required
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                    placeholder="C01ABC23DEF"
                  />
                </label>

                <label className="field">
                  <span className="field__label">CHANNEL NAME · OPTIONAL, FOR DISPLAY</span>
                  <input
                    className="field__control mono"
                    value={channelName}
                    onChange={(e) => setChannelName(e.target.value)}
                    placeholder="support"
                  />
                </label>
              </div>

              <div className="card__foot">
                Create a Slack app, add the <code>chat:write</code> bot scope, install it, then
                copy the Bot User OAuth Token. Invite the bot to the channel with{' '}
                <code>/invite @YourBot</code> — connecting posts a confirmation, which is how you
                know it worked. Find the channel ID at the bottom of the channel's details.
              </div>

              <div className="card__actions">
                <button className="button button--primary" type="submit" disabled={busy}>
                  {busy && <Loader2 size={14} className="spin" />} Connect
                </button>
                <button type="button" className="button" onClick={() => setOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}

export default SlackCard;
